import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Award, ChevronRight } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { SEO } from "@/components/common/SEO";
import SmartImage from "@/components/common/SmartImage";

export default function BestOf() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [others, setOthers] = useState([]);

  useEffect(() => {
    setData(null);
    api.get(`/best-of/${slug}`).then((r) => setData(r.data));
    api.get(`/best-of`).then((r) => setOthers(r.data));
  }, [slug]);

  if (!data) return <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 text-muted-foreground" data-testid="bestof-loading">Loading…</div>;

  return (
    <div data-testid="bestof-page">
      <SEO title={`${data.title} — ranked by TrendingNewCar`}
        description={`${data.blurb} Compare specs, prices and trending scores.`}
        image={data.cars[0]?.image}/>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org", "@type": "ItemList",
          name: data.title,
          itemListElement: data.cars.map((c, i) => ({
            "@type": "ListItem", position: i + 1,
            name: `${c.brand_name} ${c.model}`,
            url: `/cars/${c.slug}`,
          })),
        })}
      </script>

      <section className="border-b border-border bg-gradient-to-br from-blue-50/60 to-white dark:from-transparent dark:to-transparent">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-700 dark:text-blue-300 font-semibold inline-flex items-center gap-1">
            <Award className="w-3 h-3"/> Ranked list
          </div>
          <h1 className="font-display text-4xl sm:text-6xl mt-4 tracking-tighter">{data.title}</h1>
          <p className="text-muted-foreground mt-4 max-w-2xl">{data.blurb}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="bestof-list">
        <div className="space-y-4">
          {data.cars.map((c, i) => {
            const score = data.scores[c.slug];
            return (
              <Link key={c.slug} to={`/cars/${c.slug}`} data-testid={`bestof-row-${c.slug}`}
                className="group rounded-3xl border border-border bg-card p-4 sm:p-6 flex flex-col sm:flex-row gap-4 items-center hover:border-blue-400 hover:shadow-lg transition-all">
                <div className="w-full sm:w-24 flex sm:flex-col items-center gap-3 sm:gap-1">
                  <div className="font-display text-4xl tracking-tighter text-muted-foreground">#{i + 1}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Rank</div>
                </div>
                <div className="w-full sm:w-52 aspect-[16/10] rounded-2xl overflow-hidden bg-secondary shrink-0">
                  <SmartImage src={c.image} alt={`${c.brand_name} ${c.model}`} label={`${c.brand_name} ${c.model}`} className="group-hover:scale-[1.04] transition-transform duration-500"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{c.brand_name} · {c.body_type}</div>
                  <div className="font-display text-2xl mt-1 tracking-tight">{c.model}</div>
                  {c.tagline && <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.tagline}</div>}
                  <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                    {c.fuel_types?.map((f) => <span key={f} className="px-2 py-1 rounded-full bg-secondary">{f}</span>)}
                    {c.specs?.seats && <span className="px-2 py-1 rounded-full bg-secondary">{c.specs.seats} seats</span>}
                    {c.specs?.mileage_kmpl && <span className="px-2 py-1 rounded-full bg-secondary">{c.specs.mileage_kmpl} kmpl</span>}
                    {c.specs?.ncap_rating && <span className="px-2 py-1 rounded-full bg-secondary">{c.specs.ncap_rating}★ NCAP</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-2xl">{formatINR(c.price_min)}</div>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">ex-showroom</div>
                  {score !== undefined && (
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 text-xs font-semibold">
                      <ChevronRight className="w-3 h-3"/> Trending {score}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Explore more presets */}
      {others.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">More lists</div>
          <h2 className="font-display text-2xl sm:text-3xl mt-2 tracking-tight">Discover other rankings</h2>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {others.filter((o) => o.slug !== slug).slice(0, 8).map((o) => (
              <Link key={o.slug} to={`/best/${o.slug}`} data-testid={`bestof-nav-${o.slug}`}
                className="rounded-2xl border border-border p-4 bg-card hover:border-blue-400 transition-all group">
                <div className="text-sm font-medium leading-snug">{o.title}</div>
                <div className="mt-2 text-xs text-blue-600 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Browse <ArrowRight className="w-3 h-3"/>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
